import { ApplicationConfig, importProvidersFrom } from '@angular/core';
import {
  PreloadAllModules,
  provideRouter,
  withInMemoryScrolling,
  withPreloading,
  withRouterConfig,
} from '@angular/router';

import {
  getAnalytics,
  provideAnalytics,
  ScreenTrackingService,
  UserTrackingService,
} from '@angular/fire/analytics';
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { connectAuthEmulator, getAuth, provideAuth } from '@angular/fire/auth';
import {
  connectFirestoreEmulator,
  initializeFirestore,
  persistentLocalCache,
  provideFirestore,
} from '@angular/fire/firestore';
import {
  connectFunctionsEmulator,
  getFunctions,
  provideFunctions,
} from '@angular/fire/functions';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import { getPerformance, providePerformance } from '@angular/fire/performance';
import {
  connectStorageEmulator,
  getStorage,
  provideStorage,
} from '@angular/fire/storage';
import { environment } from '../environments/environment';
import { inMemoryScrollConfig, rootRoutes } from './app-routes';
import { IconModule } from '@shared/modules/icon.module';
import { provideHttpClient } from '@angular/common/http';
import {
  CLIENT_WEB_APP_PROJECT,
  SleipNgrxStoreModule,
} from '@sleipai/sleip-ngrx-store';
import {
  BrowserAnimationsModule,
  provideAnimations,
} from '@angular/platform-browser/animations';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { MAT_SNACK_BAR_DEFAULT_CONFIG } from '@shared/config/mat-snack-bar-config';
import { enableMapSet } from 'immer';

const useEmulators = environment.hmr;

enableMapSet();

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      rootRoutes,
      withPreloading(PreloadAllModules),
      withRouterConfig({
        paramsInheritanceStrategy: 'always',
      }),
      withInMemoryScrolling(inMemoryScrollConfig),
    ),
    provideHttpClient(),
    importProvidersFrom(
      SleipNgrxStoreModule.forRoot(environment),
      IconModule,
      BrowserAnimationsModule,
    ),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAnalytics(() => getAnalytics()),
    provideAuth(() => {
      const fireAuth = getAuth(getApp());
      if (useEmulators) {
        connectAuthEmulator(fireAuth, `http://localhost:9099`, {
          disableWarnings: true,
        });
      }
      return fireAuth;
    }),
    provideFirestore(() => {
      const firestore = initializeFirestore(getApp(), {
        experimentalForceLongPolling: useEmulators ? true : false,
        localCache: persistentLocalCache({
          // one tab 'owns' the synchronization at a time.
          cacheSizeBytes: 50 * 1024 * 1024, // 50MB limit
        }),
      });
      if (useEmulators) {
        connectFirestoreEmulator(firestore, 'localhost', 8080);
      }
      return firestore;
    }),
    provideFunctions(() => {
      const functions = getFunctions(getApp(), 'europe-west1');
      if (useEmulators) {
        connectFunctionsEmulator(functions, 'localhost', 5001);
      }
      return functions;
    }),
    provideMessaging(() => getMessaging()),
    providePerformance(() => getPerformance()),
    provideStorage(() => {
      const storage = getStorage();
      if (useEmulators) {
        connectStorageEmulator(storage, 'localhost', 9199);
      }
      return storage;
    }),
    provideAnimations(),
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: MAT_SNACK_BAR_DEFAULT_CONFIG,
    },
    {
      provide: CLIENT_WEB_APP_PROJECT,
      useValue: environment.clientWebAppProject,
    },
    ScreenTrackingService,
    UserTrackingService,
  ],
};
